'use client';

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import router from 'next/router';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { capitalize } from 'lodash';
import { ServerSideErrorModal, LoadingView } from '@web/components';
import {
  freshModalsContent,
  dryModalsContent,
} from '@web/testing/modalConstants';
import { useMealPlansCombinations } from '../../shopify';
import { ApiResult, wrapErrorWithResult } from '../../api';
import { OnboardingIntro } from '../OnboardingIntro';
import { QuizAbandonerMenu } from '../QuizAbandonerMenu';
import { PupNameForm } from '../PupNameForm';
import { PupGenderForm } from '../PupGenderForm';
import { PupBreedForm } from '../PupBreedForm';
import { PupStatusForm } from '../PupStatusForm';
import { CaloriesForm } from '../CaloriesForm';
import { ActivityForm } from '../ActivityForm';
import planHeaderPhoto from '../../images/product-photos/pack-shots/pack-on-board-with-shadow.png';
import { QuizHeader } from '../QuizHeader';
import { CarouselMealPlanForm } from '../CarouselMealPlanForm';
import { AllergyForm } from '../AllergyForm';
import { useAllergies } from '../../allergies';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { ExtrasSelectionForm } from '../ExtrasSelectionForm';
import { GiftWithPurchaseForm } from '../GiftWithPurchaseForm';
import { OnboardingInterstitial } from '../OnboardingInterstitial';
import { dryRecipes } from '../constants';
import { BC_TO_SHOPIFY_SKU_MAP } from '../../constants';
import { OnboardingContext } from './context';
import {
  buildCheckoutRedirectUrl,
  buildProductTypeInterpolationSubstitutes,
  createExtrasSkipHandler,
  createExtrasSubmitHandler,
  didAllergiesChange,
  interpolations,
  mergeAddonModals,
  mergeAddonSections,
  partitionAddonsByAvailability,
  advanceFromQuestion,
  isRenderableInterstitialContent,
  resolveAbandonerMenuConfig,
  resolveOnboardingPets,
  resolveRecipeConfig,
  resolveQuizProgress,
  resolveQuizProgressStep,
  getNextBoxCheckoutDetails,
  shouldDeferMenuNavigation,
} from './helpers';
import {
  readFurthestStep,
  bucketFromFurthestStep,
  resetFurthestStepTo,
} from './furthestStep';
import {
  onCartSubmission,
  getUnavailableRecipes,
  getSelectedAllergies,
  getBreedVariant,
  getUnavailableMealPrepTypes,
  normalize,
  getRecommendedGiftForPet,
} from './functions';
import { STATE, ACTIONS, ACTION_TYPES, levelToLabel } from './constants';
import { showPupBreedStep } from './guard';
import { Main, Wrapper } from './styles';

const QuizContainer = ({
  quizStep,
  children,
  showPromoBar = false,
  progress = true,
  overrideText = '',
  centerLogo = false,
}) => {
  const { flags, showModal, setShowModal } = useContext(OnboardingContext);
  const { stepNumber, totalSteps } = quizStep
    ? resolveQuizProgressStep(flags, quizStep)
    : { stepNumber: 1, totalSteps: 1 };

  return (
    <Wrapper>
      <QuizHeader
        showPromoBar={showPromoBar}
        progress={progress}
        overrideText={overrideText}
        currentStep={stepNumber}
        totalSteps={totalSteps}
        centerLogo={centerLogo}
      />
      <Main>{children}</Main>
      <ServerSideErrorModal
        shown={showModal}
        headerText="Hmm, something's not right."
        errorMessage="We weren't able to complete your request, please refresh the page and try again."
        onCloseModal={() => setShowModal(false)}
      />
    </Wrapper>
  );
};

QuizContainer.propTypes = {
  quizStep: PropTypes.oneOf([
    'pupName',
    'pupGender',
    'pupBreed',
    'pupStatus',
    'weight',
    'activity',
    'allergies',
  ]),
  children: PropTypes.object.isRequired,
  showPromoBar: PropTypes.bool,
  progress: PropTypes.bool,
  overrideText: PropTypes.string,
  centerLogo: PropTypes.string,
};

const IntroPage = props => {
  const offer = useShopifyOfferContext();
  const { send, isFetchingOfferFlags } = useContext(OnboardingContext);
  const goBack = () => router.push('/');

  if (isFetchingOfferFlags) return null;

  return (
    <OnboardingIntro
      {...props}
      goBack={goBack}
      onContinue={() => send(ACTIONS.toPupName())}
      logoUrl="/"
      popUpText={
        props.popUpText &&
        interpolate(props.popUpText, { offer: offer.formattedAmount })
      }
    />
  );
};

export const InterstitialStep = () => {
  const {
    goBack,
    send,
    pet,
    stateKey,
    fromQuestion,
    interstitial: contextInterstitial,
  } = useContext(OnboardingContext);

  const { content, ...interstitialProps } = contextInterstitial ?? {};
  const selectedContent = getBreedVariant(content, pet?.breeds);
  const canRenderInterstitial =
    isRenderableInterstitialContent(selectedContent);

  //* Recover when we are on the interstitial state but have nothing renderable
  //* (e.g. hard refresh cleared context, no matching/default block, or a block
  //* missing images). During URL sync the parent may briefly re-render this
  //* step as lastComponent after continue/back already cleared interstitial —
  //* auto goBack there would overshoot (weight→petstatus, activity→weight loop)
  //TODO: Refactor in later PR when interstitial passes testing.
  useEffect(() => {
    if (stateKey === STATE.interstitial && !canRenderInterstitial) {
      send(ACTIONS.goBack());
    }
  }, [canRenderInterstitial, send, stateKey]);

  if (stateKey !== STATE.interstitial || !canRenderInterstitial) {
    return null;
  }

  const cmsProps = interpolate(
    { ...interstitialProps, ...selectedContent },
    interpolations(pet),
  );

  return (
    <OnboardingInterstitial
      {...cmsProps}
      goBack={goBack}
      onSubmit={() => send(ACTIONS.continueFromInterstitial())}
      previousQuestion={fromQuestion ?? ''}
    />
  );
};

const AbandonerMenu = props => {
  const {
    pet,
    cart,
    cachedCart,
    send,
    reporter,
    setShowModal,
    isRouteChanging,
    petIsLoading,
    isCombinationsLoading,
  } = useContext(OnboardingContext);
  const offer = useShopifyOfferContext();

  const isPlanDataLoading = Boolean(petIsLoading || isCombinationsLoading);
  const [pendingPlanAction, setPendingPlanAction] = useState(null);

  const { quizContent, planContent, checkoutContent, ...pageProps } = props;

  const { resolvedPets, resolvedPet } = resolveOnboardingPets({
    pet,
    cart,
    cachedCart,
  });

  const pendingTargetPetId = pendingPlanAction?.payload?.pet?.id ?? null;
  const isSwitchingPet = Boolean(
    pendingTargetPetId && pendingTargetPetId !== resolvedPet?.id,
  );
  const { isLoading: isTargetPetPlanLoading } = useMealPlansCombinations(
    isSwitchingPet ? pendingTargetPetId : null,
  );

  const furthestStep = useMemo(() => readFurthestStep(), []);
  const bucket = useMemo(
    () => bucketFromFurthestStep(furthestStep),
    [furthestStep],
  );

  const safeSend = useCallback(
    action => {
      try {
        send(action);
      } catch (error) {
        reporter?.error?.(error);
        setShowModal?.(true);
      }
    },
    [send, reporter, setShowModal],
  );

  const menuConfig = useMemo(
    () =>
      resolveAbandonerMenuConfig({
        bucket,
        resolvedPet,
        resolvedPets,
        quizContent,
        planContent,
        checkoutContent,
        pageProps,
      }),
    [
      bucket,
      resolvedPet,
      resolvedPets,
      quizContent,
      planContent,
      checkoutContent,
      pageProps,
    ],
  );

  useEffect(() => {
    if (menuConfig.shouldFallBackToIntro) {
      try {
        send(ACTIONS.toIntro());
      } catch (error) {
        reporter?.error?.(error);
      }
    }
  }, [menuConfig.shouldFallBackToIntro, send, reporter]);

  useEffect(() => {
    if (!pendingPlanAction) return;
    const loadingData = isSwitchingPet
      ? isTargetPetPlanLoading
      : isPlanDataLoading;

    if (!loadingData) {
      setPendingPlanAction(null);
      safeSend(pendingPlanAction);
    }
  }, [
    pendingPlanAction,
    isSwitchingPet,
    isTargetPetPlanLoading,
    isPlanDataLoading,
    safeSend,
  ]);

  if (menuConfig.shouldFallBackToIntro) return null;

  const handleCtaClick = action => {
    const targetPetId = action?.payload?.pet?.id;
    const switchesToAnotherPet =
      action?.type === ACTION_TYPES.toRecommendedPlan &&
      targetPetId &&
      targetPetId !== resolvedPet?.id;

    if (
      switchesToAnotherPet ||
      shouldDeferMenuNavigation({
        actionType: action?.type,
        isPlanDataLoading,
      })
    ) {
      setPendingPlanAction(action);
      return;
    }

    safeSend(action);
  };

  const secondaryCtaButtons = menuConfig.secondaryActions.map(
    ({ action, secondaryCtaTextTemplate, pet: actionPet }) => ({
      secondaryCtaText: interpolate(
        secondaryCtaTextTemplate,
        interpolations(actionPet),
      ),
      onSecondaryCtaClick: () => handleCtaClick(action),
    }),
  );

  const cmsProps = interpolate(
    { ...pageProps, ...menuConfig.content },
    { ...interpolations(resolvedPet), offer: offer.formattedAmount },
  );

  return (
    <QuizAbandonerMenu
      {...cmsProps}
      multipetBannerText={menuConfig.multipetBannerText}
      onPrimaryCtaClick={() => handleCtaClick(menuConfig.primaryAction)}
      secondaryCtaButtons={secondaryCtaButtons}
      isCtaLoading={Boolean(isRouteChanging) || Boolean(pendingPlanAction)}
      petCount={resolvedPets?.length}
      furthestStep={furthestStep}
      bucket={bucket}
    />
  );
};

const PupName = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack, flags } =
    useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);
  const { showInterstitial, interstitial, ...questionProps } = props;
  const defaultValues = {
    name: pet?.name,
  };

  const onSubmit = async ({ name }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ name });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        advanceFromQuestion({
          send,
          showInterstitial,
          interstitial,
          fromQuestion: 'pupName',
          pet: payload.pet,
          pets: payload.pets,
          nextAction: ACTIONS.toPupGender({ pet: payload.pet }),
        });
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  return (
    <QuizContainer quizStep="pupName">
      <PupNameForm
        index={getStepNumber('pupName')}
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...questionProps}
      />
    </QuizContainer>
  );
};

const PupGender = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack, flags } =
    useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);
  const { showInterstitial, interstitial, ...questionProps } = props;

  const defaultValues = {
    gender: pet?.gender,
  };
  const genderList = [
    { name: 'Female', value: 'Female' },
    { name: 'Male', value: 'Male' },
  ];
  const onSubmit = async ({ gender }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ gender });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        const nextAction = showPupBreedStep({ flags }, { payload: {} })
          ? ACTIONS.toPupBreed
          : ACTIONS.toPupStatus;
        advanceFromQuestion({
          send,
          showInterstitial,
          interstitial,
          fromQuestion: 'pupGender',
          pet: payload.pet,
          pets: payload.pets,
          nextAction: nextAction({ pet: payload.pet }),
        });
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(questionProps, interpolations(pet)),
    index: getStepNumber('pupGender'),
  };

  return (
    <QuizContainer quizStep="pupGender">
      <PupGenderForm
        genderList={genderList}
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const PupBreed = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack, flags } =
    useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);
  const { showInterstitial, interstitial, ...questionProps } = props;

  const defaultValues = {
    breeds:
      pet?.breeds?.map(breed => ({
        value: breed.id,
        label: breed.name,
      })) ?? [],
  };

  const onSubmit = async ({ breeds }) => {
    let result;
    let payload;
    const breed_ids = breeds.map(({ value }) => value);

    try {
      const response = await savePet({ breed_ids });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        advanceFromQuestion({
          send,
          showInterstitial,
          interstitial,
          fromQuestion: 'pupBreed',
          pet: payload.pet,
          pets: payload.pets,
          nextAction: ACTIONS.toPupStatus({ pet: payload.pet }),
        });
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const interpolatedProps = interpolate(questionProps, interpolations(pet));
  const cmsProps = {
    ...interpolatedProps,
    index: getStepNumber('pupBreed'),
    labelBreed: interpolatedProps.labelName,
  };

  return (
    <QuizContainer quizStep="pupBreed">
      <PupBreedForm
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const PupStatus = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack, flags } =
    useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);
  const { showInterstitial, interstitial, ...questionProps } = props;

  const defaultValues = {
    spayedStatus: pet?.is_neutered?.toString(),
  };

  const onSubmit = async ({ spayedStatus }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ is_neutered: spayedStatus });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        advanceFromQuestion({
          send,
          showInterstitial,
          interstitial,
          fromQuestion: 'pupStatus',
          pet: payload.pet,
          pets: payload.pets,
          nextAction: ACTIONS.toWeight({ pet: payload.pet }),
        });
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(questionProps, interpolations(pet)),
    index: getStepNumber('pupStatus'),
  };

  return (
    <QuizContainer quizStep="pupStatus">
      <PupStatusForm
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...cmsProps}
        pet={pet}
      />
    </QuizContainer>
  );
};

const Weight = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    flags,
  } = useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);
  const { showInterstitial, interstitial, ...questionProps } = props;
  const defaultValues = {
    weight: pet?.weight,
    idealWeight: pet?.ideal_weight,
  };

  const onSubmit = async ({ weight, idealWeight }) => {
    let result, payload;
    try {
      const response = await savePet({
        ideal_weight: idealWeight,
        weight,
      });
      payload = {
        action: 'update',
        pet: response.data.pet,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        advanceFromQuestion({
          send,
          showInterstitial,
          interstitial,
          fromQuestion: 'weight',
          pet: payload.pet,
          nextAction: ACTIONS.toActivity({ pet: payload.pet }),
        });
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(questionProps, interpolations(pet)),
    index: getStepNumber('weight'),
  };

  cmsProps.weightCallout = getBreedVariant(cmsProps.weightCallout, pet?.breeds);

  const isPuppy = false;

  return (
    <QuizContainer quizStep="weight">
      <CaloriesForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
        isPuppy={isPuppy}
        pet={pet}
      />
    </QuizContainer>
  );
};

const Activity = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    flags,
  } = useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);
  const { showInterstitial, interstitial, ...questionProps } = props;
  const defaultValues = {
    activity: pet?.activity_level,
  };
  const onSubmit = async ({ activity }) => {
    let result, payload;
    try {
      const response = await savePet({ activity_level: activity });

      payload = {
        action: 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        advanceFromQuestion({
          send,
          showInterstitial,
          interstitial,
          fromQuestion: 'activity',
          pet: payload.pet,
          pets: payload.pets,
          nextAction: ACTIONS.toAllergies({ pet: payload.pet }),
        });
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(questionProps, interpolations(pet)),
    index: getStepNumber('activity'),
  };
  return (
    <QuizContainer quizStep="activity">
      <ActivityForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
        pet={pet}
      />
    </QuizContainer>
  );
};

const Allergies = props => {
  const {
    pet,
    cart,
    send,
    goBack,
    goToLogin,
    savePet,
    reporter,
    setShowModal,
    refreshExtras,
    refreshPlans,
    flags,
  } = useContext(OnboardingContext);
  const { getStepNumber } = resolveQuizProgress(flags);
  const { showInterstitial, interstitial, ...questionProps } = props;

  const allAllergies = useAllergies();

  const hasPetAllergies = pet?.allergies && pet?.allergies.length > 0;

  const noAllergiesSelected = `pet:${pet?.id}:no_allergy`;

  const userSelectedNo =
    typeof window !== 'undefined' && localStorage.getItem(noAllergiesSelected);

  const selectedAllergies = useMemo(() => {
    return hasPetAllergies
      ? normalize(getSelectedAllergies(pet?.allergies, allAllergies))
      : [];
  }, [hasPetAllergies, pet?.allergies, allAllergies]);

  const defaultValues = useMemo(
    () => ({
      //* For allergy radio (yes/no)
      allergy: hasPetAllergies
        ? 'true'
        : pet?.starter_box || userSelectedNo
        ? 'false'
        : null,
      //* For allergy selection (multiselect)
      allergies: selectedAllergies,
    }),
    [hasPetAllergies, selectedAllergies, pet?.starter_box, userSelectedNo],
  );

  const onSubmit = async ({ allergy, allergies }) => {
    let result, payload;
    let allergyIds = undefined;

    if (allergy === 'true') {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(noAllergiesSelected);
      }

      allergyIds = allergies?.map(({ value }) => value);
    } else if (allergy === 'false') {
      if (typeof window !== 'undefined') {
        localStorage.setItem(noAllergiesSelected, 'true');
      }
    }

    const allergiesChanged = didAllergiesChange(
      pet?.allergies,
      allergies,
      allergy,
    );

    try {
      if (allergiesChanged) {
        const response = await savePet({
          allergy_ids:
            allergyIds === undefined || allergyIds.length === 0
              ? undefined
              : allergyIds,
        });

        // Response from savePet above resets starter box;
        // reset furthest step to prevent pet from reaching
        // checkout without a starter box
        resetFurthestStepTo('recommendedPlan');

        payload = {
          action: 'update',
          pet: response.data.pet,
          pets: response.data.pets,
          allergies: allergies?.map(({ label }) => label) || [],
          allergyIds: allergyIds,
        };
      } else {
        payload = {
          action: 'update',
          pet,
          pets: cart?.pets,
          allergies: allergies?.map(({ label }) => label) || [],
          allergyIds: allergyIds,
        };
      }

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        advanceFromQuestion({
          send,
          showInterstitial,
          interstitial,
          fromQuestion: 'allergies',
          pet: payload.pet,
          pets: payload.pets,
          nextAction: ACTIONS.toRecommendedPlan({
            pet: payload.pet,
            pets: payload.pets,
            cart,
            ...payload,
          }),
        });
        if (allergiesChanged) {
          refreshPlans();
          refreshExtras();
        }
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });

    return result;
  };

  const cmsProps = {
    ...interpolate(questionProps, interpolations(pet)),
    index: getStepNumber('allergies'),
  };

  return (
    <QuizContainer quizStep="allergies">
      <AllergyForm
        defaultValues={defaultValues}
        placeholderAllergiesCount={9}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
        pet={pet}
      />
    </QuizContainer>
  );
};

const RecommendedPlan = props => {
  const {
    setShowModal,
    reporter,
    elevarReporter,
    combinationsByPlanType,
    pet,
    getPetPronoun,
    joinWithOr,
    pet_id: petId,
    cartApi,
    attachFreeGifts,
    queueRequest,
    send,
    service,
    pricePeriod,
    goBack,
    setLocalSelection,
    mealPlanForPet,
    flags: {
      hideMixed,
      showGiftOnboardingStep,
      showFreeGiftSelectionExtension,
    },
    campaignId,
    cart,
  } = useContext(OnboardingContext);
  const { type, amount, formattedAmount } = useShopifyOfferContext();

  let combinationId = null;
  const quizCompleteLogged = useRef(false);
  const defaultPlanType = mealPlanForPet
    ? mealPlanForPet.plan_type_name
    : 'fresh';
  const unavailableFreshRecipes = getUnavailableRecipes(
    combinationsByPlanType.fresh?.availableRecipes?.fresh || [],
  );
  const unavailableBakedRecipes = getUnavailableRecipes(
    combinationsByPlanType.baked?.availableRecipes?.baked || [],
  );
  const freshRecipes = props.recipesList;
  const bakedRecipes = props.bakedRecipesList?.length
    ? props.bakedRecipesList
    : dryRecipes;

  const getSelectableRecipes = (
    recipesToFilter = [],
    availableRecipes = [],
    unavailableRecipes = [],
  ) => {
    return recipesToFilter.filter(recipe => {
      const recipeItem = availableRecipes.find(item => item.value === recipe);
      return (
        Boolean(recipeItem) &&
        !recipeItem.outOfStock &&
        !unavailableRecipes.includes(recipe)
      );
    });
  };

  const cmsDefaultFreshRecipes = getSelectableRecipes(
    props.defaultFreshRecipes,
    freshRecipes,
    unavailableFreshRecipes,
  );

  const cmsDefaultBakedRecipes = getSelectableRecipes(
    props.defaultBakedRecipes,
    bakedRecipes,
    unavailableBakedRecipes,
  );

  const backendDefaultFreshRecipes = getSelectableRecipes(
    combinationsByPlanType.fresh?.recommendedCombination?.recipes?.fresh,
    freshRecipes,
    unavailableFreshRecipes,
  );

  const backendDefaultBakedRecipes = getSelectableRecipes(
    combinationsByPlanType.mixed?.recommendedCombination?.recipes?.baked,
    bakedRecipes,
    unavailableBakedRecipes,
  );
  const defaultFreshRecipes = mealPlanForPet
    ? mealPlanForPet.recipe_selections.fresh
    : cmsDefaultFreshRecipes.length > 0
    ? cmsDefaultFreshRecipes
    : backendDefaultFreshRecipes ?? [];
  const defaultBakedRecipes =
    mealPlanForPet?.plan_type_name === 'mixed'
      ? mealPlanForPet.recipe_selections.baked
      : cmsDefaultBakedRecipes.length > 0
      ? cmsDefaultBakedRecipes
      : backendDefaultBakedRecipes ?? [];
  const unavailableMealPrepTypes = getUnavailableMealPrepTypes(
    combinationsByPlanType,
  );

  const getConfigFromRecipes = (
    selectedMealPrepType,
    selectedFreshRecipes,
    selectedBakedRecipes,
  ) => {
    const config = resolveRecipeConfig({
      combinationsByPlanType,
      selectedMealPrepType,
      selectedFreshRecipes,
      selectedBakedRecipes,
      offerType: type,
      offerAmount: amount,
    });

    if (config?.combination_id) {
      combinationId = config.combination_id;
    }

    return config;
  };

  const productTypeInterpolationSubstitutes =
    buildProductTypeInterpolationSubstitutes({
      combinationsByPlanType,
      offerType: type,
      offerAmount: amount,
    });

  const couponCode = cart?.coupon_code?.[0];
  const giftType = props.giftTypes?.[0] ?? null;

  //check if the pet has a whitelisted gift already so that so it is not cleared
  //by the default if the pet plan is edited
  const petHasEligibleGiftInCart = pet?.free_gifts?.some(gift =>
    giftType?.choices?.some(
      choice => choice.value === gift.sku && !choice.disabled,
    ),
  );

  const recommendedGWPSku =
    showFreeGiftSelectionExtension && !petHasEligibleGiftInCart
      ? getRecommendedGiftForPet(giftType, pet?.weight, couponCode)?.value
      : null;

  const [isAttachingGift, setIsAttachingGift] = useState(true);

  useEffect(() => {
    if (!recommendedGWPSku) {
      setIsAttachingGift(false);
      return;
    }

    setIsAttachingGift(true);

    queueRequest(async () => {
      const giftResult =
        (await attachFreeGifts(petId, {
          skus: [recommendedGWPSku],
        })) ?? ApiResult.error.client('Failed to attach gift');
      ApiResult.ifError(giftResult, error => reporter.error(error.original));
      setIsAttachingGift(false);
      return giftResult;
    });
  }, [recommendedGWPSku, petId, attachFreeGifts, queueRequest, reporter]);

  const onMealPlanAndRecipesSubmit = async (
    planType,
    freshRecipes,
    bakedRecipes,
  ) => {
    const selectedPlanTypeCombinations = combinationsByPlanType[planType];

    const nextBoxCheckoutDetails = getNextBoxCheckoutDetails(
      selectedPlanTypeCombinations,
      combinationId,
    );

    if (planType === 'mixed') {
      send(
        ACTIONS.selectedMixedRecipes(
          {
            recipes: {
              dry: bakedRecipes,
              fresh: freshRecipes,
            },
            planType: planType,
            flags: { showGiftOnboardingStep },
          },
          {
            combinationsByPlanType,
            combinationId,
            cartProduct: mealPlanForPet,
            cartApi,
            reporter,
            petId,
            setShowModal,
            setLocalSelection,
            campaignId,
            ...nextBoxCheckoutDetails,
          },
        ),
      );
    } else {
      send(
        ACTIONS.selectedFreshRecipes(
          {
            recipes: freshRecipes,
            planType: planType,
            flags: { showGiftOnboardingStep },
          },
          {
            combinationsByPlanType,
            combinationId,
            cartProduct: mealPlanForPet,
            cartApi,
            reporter,
            petId,
            setShowModal,
            setLocalSelection,
            campaignId,
            ...nextBoxCheckoutDetails,
          },
        ),
      );
    }
    return onCartSubmission(service);
  };

  const mealPlanDescription = `We created a meal plan just for ${capitalize(
    pet.name,
  )}—a ${pet.weight}-pound ${levelToLabel[pet.activity_level]} pup—${
    pet.allergies.length > 0
      ? `and ensured no ${joinWithOr(
          pet.allergies.map(a => a.name.toLowerCase()),
        )} was in ${getPetPronoun(3, 'POSSESSIVE_ADJECTIVE')} recipes `
      : ''
  }so ${getPetPronoun(
    3,
    'SUBJECT',
  )} gets the perfect portions to stay healthy, happy, and thriving.`;

  const carouselMealPlanProps = {
    onSubmit: onMealPlanAndRecipesSubmit,
    headline: interpolate(props.headline, interpolations(pet)),
    subheadline: props.subheadline,
    description:
      interpolate(props.description, interpolations(pet)) ??
      mealPlanDescription,
    petName: pet?.name,
    ctaText: interpolate(props.ctaText, { offer: formattedAmount }),
    modals: [...freshModalsContent, ...dryModalsContent],
    freshRecipes: freshRecipes,
    defaultFreshRecipes: defaultFreshRecipes ?? [],
    bakedRecipes: bakedRecipes,
    defaultBakedRecipes: defaultBakedRecipes ?? [],
    defaultMealPrepType: defaultPlanType,
    mealPrepTypes: interpolate(
      props.mealPrepTypes,
      productTypeInterpolationSubstitutes,
    ),
    collapsedMealPrepTypes: interpolate(
      props.collapsedMealPrepTypes,
      productTypeInterpolationSubstitutes,
    ),
    unavailableMealPrepTypes: unavailableMealPrepTypes,
    unavailableFreshRecipes: unavailableFreshRecipes,
    unavailableBakedRecipes: unavailableBakedRecipes,
    onBackButtonClick: goBack,
    period: pricePeriod,
    getConfigFromRecipes: getConfigFromRecipes,
    body: props.body,
    headerImage: planHeaderPhoto,
    modalContent: interpolate(
      props.mealPlanModalContent,
      productTypeInterpolationSubstitutes,
    ),
    notSelectableText: `Based on your answers, this meal plan is unavailable for ${pet.name}.`,
    ctaButtonVariant: 'primary',
    hideMixed: hideMixed,
    pet,
    recipeModalsList: props.recipeModalsList,
    recipeSelectionText: props.recipeSelectionText,
    callouts: props.callouts,
    ctaDisabled: isAttachingGift,
  };

  // Only fire quiz complete for new pets, not edits
  useEffect(() => {
    if (quizCompleteLogged.current) return;
    if (!petId || mealPlanForPet) return;
    if (typeof elevarReporter?.elevarCompleteQuiz !== 'function') return;
    try {
      elevarReporter.elevarCompleteQuiz();
      quizCompleteLogged.current = true;
    } catch (error) {
      reporter?.error?.(error, { contexts: { elevar: 'completeQuiz' } });
    }
  }, [elevarReporter, reporter, petId, mealPlanForPet]);

  return (
    <QuizContainer progress={false} centerLogo={true}>
      <CarouselMealPlanForm
        {...carouselMealPlanProps}
        carousel={props.carousel}
      />
    </QuizContainer>
  );
};

RecommendedPlan.displayName = 'RecommendedPlan';
RecommendedPlan.propTypes = {};

const GiftWithPurchase = props => {
  const {
    cart,
    pet,
    pet_id: petId,
    send,
    goBack,
    reporter,
    setShowModal,
    attachFreeGifts,
    isRouteChanging,
  } = useContext(OnboardingContext);
  const couponCode = cart?.coupon_code?.[0];
  const giftType = props.giftTypes?.[0] ?? {};
  const choices = giftType.choices ?? [];

  const addedGiftSkus = pet?.free_gifts?.map(gift => gift.sku) ?? [];
  const selectedVariant =
    choices.find(choice => addedGiftSkus.includes(choice.value)) ?? null;

  const recommendedChoice = getRecommendedGiftForPet(
    giftType,
    pet?.weight,
    couponCode,
  );

  const onSubmit = async ({ gift }) => {
    if (!gift) {
      return ApiResult.error.client('No gift selected');
    }

    const attachResult = await attachFreeGifts(petId, {
      skus: [gift],
    });

    const result =
      attachResult ?? ApiResult.error.client('Failed to attach gift');

    ApiResult.match(result, {
      success: () => {
        send(ACTIONS.toExtras());
      },
      error: {
        client: error => {
          if (error.original) {
            reporter.error(error.original);
          }
          setShowModal(true);
        },
        server: () => setShowModal(true),
      },
    });

    return result;
  };

  const cmsProps = {
    ...interpolate(props, {
      ...interpolations(pet),
      giftName: giftType.name ?? '',
      choiceName: recommendedChoice?.name?.toLowerCase() ?? '',
    }),
  };

  return (
    <QuizContainer progress={false} centerLogo={true}>
      <GiftWithPurchaseForm
        onSubmit={onSubmit}
        goBack={goBack}
        isRouteChanging={isRouteChanging}
        giftType={giftType}
        choices={choices}
        recommendedChoice={recommendedChoice}
        defaultValues={{
          gift:
            (selectedVariant?.disabled ? null : selectedVariant?.value) ??
            recommendedChoice?.value ??
            '',
        }}
        {...cmsProps}
        pet={pet}
      />
    </QuizContainer>
  );
};

const Extras = props => {
  const {
    reporter,
    goBack,
    setShowModal,
    pet,
    pet_id: petId,
    attachExtras,
    send,
    pets,
    selectedAddons,
    treatProducts,
    supplementProducts,
    dentalChews,
    treatsForLife,
  } = useContext(OnboardingContext);
  const { formattedAmount, type, amount } = useShopifyOfferContext();
  const treatsAndDentalChews = [...treatProducts, ...dentalChews];
  const allAddonProducts = [...supplementProducts, ...treatsAndDentalChews];
  const { available: defaultAddons, unavailable: unavailableAddons } =
    partitionAddonsByAvailability(
      selectedAddons,
      allAddonProducts,
      BC_TO_SHOPIFY_SKU_MAP,
    );

  const mergedAddonSection = mergeAddonSections(props.addonSections, {
    supplementProducts,
    treatsAndDentalChews,
    skuMap: BC_TO_SHOPIFY_SKU_MAP,
    offerType: type,
    offerAmount: amount,
    formattedAmount,
    petName: pet.name,
  });

  const mergedModals = mergeAddonModals(props.modals, {
    supplementProducts,
    treatsAndDentalChews,
    skuMap: BC_TO_SHOPIFY_SKU_MAP,
    offerType: type,
    offerAmount: amount,
    formattedAmount,
  });

  const onSubmit = createExtrasSubmitHandler({
    attachExtras,
    petId,
    reporter,
    send,
    pet,
    pets,
    skuMap: BC_TO_SHOPIFY_SKU_MAP,
    setShowModal,
  });

  const onSkip = createExtrasSkipHandler({
    submitExtras: onSubmit,
    reporter,
    send,
    pet,
    pets,
    selectedAddons,
    unavailableAddons,
  });

  return (
    <QuizContainer progress={false} centerLogo={true}>
      <ExtrasSelectionForm
        headline={interpolate(props.headline, { pupName: pet.name })}
        subheadline={interpolate(props.subheadline, {
          pupName: pet.name,
        })}
        addonSections={mergedAddonSection}
        defaultAddons={defaultAddons}
        unavailableAddons={unavailableAddons}
        onSubmit={onSubmit}
        onSkipButtonClick={onSkip}
        onBackButtonClick={goBack}
        submitButtonText={props.desktopCtaText}
        modals={mergedModals}
        ctaButtonVariant="primary"
        centerSkip={true}
        treatsForLife={treatsForLife}
        pillText={interpolate(props.pillText, { offer: formattedAmount })}
        pillVariant={props.pillVariant}
        body={props.body}
      />
    </QuizContainer>
  );
};

Extras.propTypes = {
  headline: PropTypes.string,
  addonSections: PropTypes.array,
  desktopCtaText: PropTypes.string,
  modals: PropTypes.array,
};

const Checkout = () => {
  const { cart, customer } = useContext(OnboardingContext);
  const checkoutUrl = cart?.checkout_url;
  const customerEmail = customer?.email;

  useEffect(() => {
    let anonymousId = '';
    if (window.analytics && window.analytics.user) {
      anonymousId = window.analytics.user().anonymousId();
    }

    const redirectUrl = buildCheckoutRedirectUrl({
      checkoutUrl,
      locationSearch: window.location?.search,
      anonymousId,
      customerEmail,
    });

    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, [customerEmail, checkoutUrl]);
  return <LoadingView variant="checkout" />;
};

export const components = {
  [STATE.intro]: IntroPage,
  [STATE.pupName]: PupName,
  [STATE.pupGender]: PupGender,
  [STATE.pupBreed]: PupBreed,
  [STATE.pupStatus]: PupStatus,
  [STATE.weight]: Weight,
  [STATE.activity]: Activity,
  [STATE.allergies]: Allergies,
  [STATE.interstitial]: InterstitialStep,
  [STATE.recommendedPlan]: RecommendedPlan,
  [STATE.giftWithPurchase]: GiftWithPurchase,
  [STATE.quizAbandonerMenu]: AbandonerMenu,
  [STATE.extras]: Extras,
  [STATE.checkout]: Checkout,
};
